import { memo, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaRegBookmark, FaBookmark } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

import {
  followStockThunk,
  unfollowStockThunk,
} from "../../redux/user/watchlist/watchlistActions";

/* -----------------------------
   Stable fallback references
------------------------------ */
const EMPTY_ARRAY = [];

const WatchlistButton = memo(({ userId, stockId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [localLoading, setLocalLoading] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  /* -----------------------------
     Redux selectors (PRIMITIVES ONLY)
  ------------------------------ */
  const currentQuery = useSelector(
    (state) => state.search?.currentQuery ?? ""
  );

  const currentSymbol = useSelector(
    (state) => state.stockData?.currentSymbol ?? ""
  );

  const currentStockId = useSelector(
    (state) => state.stockData?.currentStock?.id ?? null
  );

  const watchlistItems = useSelector(
    (state) => state.watchlist?.items ?? EMPTY_ARRAY
  );

  const watchlistStocks = useSelector(
    (state) => state.watchlist?.stocks ?? EMPTY_ARRAY
  );

  const globalLoading = useSelector(
    (state) => state.watchlist?.loading ?? false
  );

  /* -----------------------------
     Derived values
  ------------------------------ */
  const normalizedSymbol = useMemo(() => {
    const symbol = currentQuery || currentSymbol;
    return symbol ? symbol.toUpperCase() : null;
  }, [currentQuery, currentSymbol]);

  const resolvedStockId = useMemo(() => {
    return stockId ?? currentStockId ?? null;
  }, [stockId, currentStockId]);

  const loading = globalLoading || localLoading;

  /* -----------------------------
     Watchlist lookup (O(1))
  ------------------------------ */
  const watchlistLookup = useMemo(() => {
    const set = new Set();

    for (const item of watchlistItems) {
      if (item?.id) set.add(item.id);
      if (item?.symbol) set.add(item.symbol.toUpperCase());
    }

    for (const symbol of watchlistStocks) {
      if (typeof symbol === "string") {
        set.add(symbol.toUpperCase());
      }
    }

    return set;
  }, [watchlistItems, watchlistStocks]);

  const isInWatchlist = useMemo(() => {
    if (resolvedStockId && watchlistLookup.has(resolvedStockId)) {
      return true;
    }
    if (normalizedSymbol && watchlistLookup.has(normalizedSymbol)) {
      return true;
    }
    return false;
  }, [resolvedStockId, normalizedSymbol, watchlistLookup]);

  /* -----------------------------
     Click handler
  ------------------------------ */
  const handleClick = useCallback(
    async (e) => {
      e.stopPropagation();

      if (loading || !userId) return;
      if (!resolvedStockId && !normalizedSymbol) return;

      const thunk = isInWatchlist
        ? unfollowStockThunk
        : followStockThunk;

      try {
        setLocalLoading(true);

        if (resolvedStockId) {
          await dispatch(
            thunk({ userId, stockId: resolvedStockId })
          ).unwrap();
        } else {
          await dispatch(
            thunk({ userId, symbol: normalizedSymbol })
          ).unwrap();
        }
        if (!isInWatchlist) setJustAdded(true);
      } catch (err) {
        console.warn("[WatchlistButton] Error:", err);
      } finally {
        setLocalLoading(false);
      }
    },
    [
      loading,
      userId,
      resolvedStockId,
      normalizedSymbol,
      isInWatchlist,
      dispatch,
    ]
  );

  /* -----------------------------
     Guard
  ------------------------------ */
  if (!resolvedStockId && !normalizedSymbol) {
    return null;
  }

  /* -----------------------------
     Render
  ------------------------------ */
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      aria-pressed={isInWatchlist}
      title={isInWatchlist ? t('common.removeFromWatchlist') : t('common.addToWatchlist')}
      onAnimationEnd={() => setJustAdded(false)}
      className={`
        relative p-2 sm:p-2.5 rounded-lg transition-all duration-300 border
        flex items-center justify-center
        ${
          isInWatchlist
            ? "bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100 dark:bg-blue-500/20 dark:border-blue-500/40 dark:text-blue-300 dark:hover:bg-blue-500/30"
            : "bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200 dark:bg-slate-600/30 dark:border-slate-500/30 dark:text-slate-300 dark:hover:bg-slate-500/40"
        }
        disabled:opacity-50 disabled:cursor-not-allowed
        hover:scale-110 active:scale-95
        ${justAdded ? "animate-pop" : ""}
      `}
    >
      {justAdded && (
        <span className="absolute inset-0 rounded-lg border-2 border-blue-400 animate-ring-burst pointer-events-none" aria-hidden="true" />
      )}
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : isInWatchlist ? (
        <FaBookmark className="w-4 h-4" />
      ) : (
        <FaRegBookmark className="w-4 h-4" />
      )}
    </button>
  );
});

WatchlistButton.displayName = "WatchlistButton";

export default WatchlistButton;
